import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, merge, Observable, share, take } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { JobsChannel } from '@recruitee/account';
import { AsyncJobType } from '@recruitee/account-data';
import { filterBoolean, RtcErrorHandler } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';
import { AppStore } from '@recruitee/legacy-app-store';
import { fromChannel } from '@recruitee/websocket';

import { AsyncJob } from './async-jobs.types';
import { AsyncJobActions, AsyncJobSelectors } from './store/async-job.store';

@Injectable({ providedIn: 'root' })
export class AsyncJobService {
  private jobFinish$ = this.actions$.pipe(ofType(AsyncJobActions.finish), share());

  constructor(
    private appStore: AppStore,
    private i18n: I18nService,
    private store: Store,
    private actions$: Actions,
    private errorHandler: RtcErrorHandler,
  ) {}

  public init(wsChannel$: BehaviorSubject<JobsChannel | null>) {
    this.subscribeToWebSocketsChannel(wsChannel$);
  }

  public subscribeToWebSocketsChannel(wsChannel$: BehaviorSubject<JobsChannel | null>): void {
    wsChannel$
      .pipe(
        filterBoolean,
        switchMap(channel => this.handleAsyncJobEvents$(channel)),
      )
      .subscribe({
        error: err => {
          console.error(err);
        },
      });
  }

  public getJob$(uuid: string) {
    return this.store.select(AsyncJobSelectors.byId(uuid));
  }

  public getJobs$(type: AsyncJobType) {
    return this.store.select(AsyncJobSelectors.entities({ scope: type }));
  }

  public getPendingJobs(): Observable<AsyncJob[]> {
    return this.store.select(AsyncJobSelectors.pendingJobs);
  }

  public trackAsyncJobProgress(type: AsyncJobType) {
    const until$ = (uuid: string) =>
      this.jobFinish$.pipe(filter(({ payload }) => payload.job.uuid === uuid));

    return ({ uuid }: { uuid: string }) => {
      this.store.dispatch(AsyncJobActions.create({ payload: { job: { uuid, type } } }));
      return this.store
        .select(AsyncJobSelectors.byId(uuid))
        .pipe(filterBoolean, takeUntil(until$(uuid)));
    };
  }

  public getJobFinishState$(type: AsyncJobType): Observable<AsyncJob> {
    return this.jobFinish$.pipe(
      filter(({ payload }) => payload.job.type === type),
      switchMap(({ payload }) => this.getJob$(payload.job.uuid).pipe(filterBoolean, take(1))),
    );
  }

  public getJobFinishStateById$(uuid: string): Observable<AsyncJob> {
    return this.jobFinish$.pipe(
      filter(({ payload }) => payload.job.uuid === uuid),
      switchMap(({ payload }) => this.getJob$(payload.job.uuid).pipe(filterBoolean, take(1))),
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private handleAsyncJobEvents$(channel: JobsChannel) {
    return merge(
      fromChannel(channel, 'job:started').pipe(
        tap(jobInfo => this.store.dispatch(AsyncJobActions.start({ payload: { job: jobInfo } }))),
        tap(jobInfo => {
          // Debugging Sentry issue
          if (!jobInfo.type || !jobInfo.uuid) {
            this.errorHandler.trace(
              `Missing jobInfo from job:started: ${JSON.stringify(jobInfo)}`,
              true,
            );
          }
        }),
      ),
      fromChannel(channel, 'job:pending').pipe(
        switchMap(jobInfo =>
          this.getJob$(jobInfo.uuid).pipe(
            take(1),
            tap(jobState => {
              const isFirstEvent = !jobState;
              const isInitialEvent = !jobInfo.count;
              if (isInitialEvent || isFirstEvent) {
                this.store.dispatch(AsyncJobActions.start({ payload: { job: jobInfo } }));
                // Debugging Sentry issue
                if (!jobInfo?.type || !jobInfo?.uuid) {
                  this.errorHandler.trace(
                    `Missing jobInfo from job:pending: ${JSON.stringify(
                      jobInfo,
                    )}; JobState from job:pending: ${JSON.stringify(jobState)}`,
                    true,
                  );
                }
              } else {
                this.store.dispatch(AsyncJobActions.pending({ payload: { job: jobInfo } }));
              }
            }),
          ),
        ),
      ),
      fromChannel(channel, 'job:finished').pipe(
        tap(job => this.store.dispatch(AsyncJobActions.finish({ payload: { job } }))),
      ),
    );
  }
}
